import { Link } from 'react-router-dom';
import { ArrowRight, Circle, Users, Sparkles, Briefcase, GraduationCap, Radio, ShieldCheck, Building2, Wifi, FileCheck } from 'lucide-react';
import Shell from '../components/Shell';
import Seo from '../components/Seo';
import HomeSlideshow from '../components/HomeSlideshow';
import { Section, DotCircle, CTA } from '../components/Pieces';
import { transformCards, transformationChain, ecceStats, evidenceStrip } from '../mock';

export default function Home() {
  return (
    <Shell>
      <Seo
        description="Mashmari builds integrated systems that connect curriculum, teachers, classrooms, learners, evidence and secure digital infrastructure — from modern schools to Anganwadis, village learning centres and broadband networks."
      />
      {/* Capability highlights carousel — sits directly below the header */}
      <HomeSlideshow />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--mm-cream)]">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] mm-dot-halo-light rounded-full opacity-70" />
        <div className="absolute -bottom-32 -left-40 w-[600px] h-[600px] mm-dot-halo-light rounded-full opacity-40" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 pb-20 md:pt-24 md:pb-28 relative">
          <div className="grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
            <div className="mm-fade-up">
              <div className="mm-kicker mb-5">Mashmari Consultants Private Limited</div>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[68px] leading-[1.02] font-bold text-[var(--mm-ink)]">
                Transforming Learning.<br/>
                <span className="text-[var(--mm-purple)]">Connecting Communities.</span><br/>
                Building Digital Trust.
              </h1>
              <p className="mt-7 text-lg md:text-xl text-[var(--mm-ink)]/70 max-w-2xl leading-relaxed">
                Mashmari builds integrated systems that connect curriculum, teachers, classrooms, learners, evidence and secure digital infrastructure — from modern schools to Anganwadis, village learning centres and broadband networks.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/projects" className="btn-primary">Explore Our Work <ArrowRight className="w-4 h-4"/></Link>
                <Link to="/transform/school" className="btn-outline">Discover the Mashmari Approach</Link>
              </div>
            </div>
            <div className="relative min-h-[380px] flex items-center justify-center">
              <TrianglePattern />
            </div>
          </div>
        </div>
      </section>

      {/* Three transformation domains */}
      <Section bg="cream">
        <div className="grid md:grid-cols-3 gap-6">
          {transformCards.map((c, i) => {
            const Icon = [GraduationCap, Users, ShieldCheck][i];
            return (
              <Link key={c.id} to={c.href} className="mm-card p-8 group">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 rounded-full bg-[var(--mm-purple)] text-white flex items-center justify-center">
                    <Icon className="w-6 h-6"/>
                  </div>
                  <span className="mm-kicker">{c.kicker}</span>
                </div>
                <h3 className="font-display text-2xl font-bold text-[var(--mm-ink)]">{c.title}</h3>
                <p className="mt-3 text-[var(--mm-ink)]/70 text-[15px] leading-relaxed">{c.body}</p>
                <div className="mt-6 btn-ghost">Learn more <ArrowRight className="w-4 h-4"/></div>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* The Mashmari Difference — chain */}
      <Section bg="white">
        <div className="text-center max-w-3xl mx-auto">
          <div className="mm-kicker mb-4">The Mashmari Difference</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] mm-rule-center">Transformation happens where learning happens.</h2>
          <p className="mt-6 text-[var(--mm-ink)]/70 text-lg leading-relaxed">Education transformation does not occur merely because a school buys an LMS, generates AI lesson plans, digitises textbooks or installs classroom screens. It happens when these capabilities converge in the classroom and improve what the teacher and learner actually do.</p>
        </div>

        <div className="mt-14 relative">
          <div className="flex flex-wrap items-stretch justify-center gap-y-4">
            {transformationChain.map((n, i) => {
              const isCore = n === 'CLASSROOM EXECUTION';
              return (
                <div key={n} className="flex items-center">
                  <div className={`px-4 py-3 rounded-full border text-center transition-all ${isCore ? 'bg-[var(--mm-purple)] text-white border-[var(--mm-purple)] shadow-[0_18px_40px_-18px_rgba(74,18,89,.5)] scale-105 md:scale-110 text-sm md:text-base font-semibold px-6 md:px-7 py-3.5' : 'bg-[var(--mm-cream)] border-[var(--mm-line)] text-[var(--mm-ink)]/85 text-xs md:text-[13px] font-medium'}`}>
                    {n}
                  </div>
                  {i < transformationChain.length - 1 && <span className="mx-1.5 md:mx-2 text-[var(--mm-purple)]/40">→</span>}
                </div>
              );
            })}
          </div>
          <p className="mt-10 text-center max-w-3xl mx-auto text-[var(--mm-ink)]/70 italic">Technology enables the journey. Classroom practice creates the outcome. Evidence improves the next cycle.</p>
        </div>
      </Section>

      {/* Cross-site story loop */}
      <Section bg="cream2">
        <div className="text-center max-w-3xl mx-auto">
          <div className="mm-kicker mb-4">A Continuous Loop</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] mm-rule-center">Understand → Design → Enable → Execute → Observe → Improve → Scale</h2>
        </div>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { t: 'Understand', b: 'Curriculum, learner, institution and context.' },
            { t: 'Design', b: 'Competencies, pathways, lessons, learning experiences.' },
            { t: 'Enable', b: 'Teachers, workers and institutions.' },
            { t: 'Execute', b: 'Deliver in the real classroom.' },
            { t: 'Observe', b: 'Capture learner, teacher and classroom evidence.' },
            { t: 'Improve', b: 'Adapt the next lesson or intervention.' },
            { t: 'Scale', b: 'Expand only after evidence demonstrates value.' },
          ].map((s, i) => (
            <div key={s.t} className="bg-white border border-[var(--mm-line)] rounded-2xl p-5">
              <div className="w-9 h-9 rounded-full bg-[var(--mm-purple)] text-white flex items-center justify-center text-xs font-bold">{String(i+1).padStart(2,'0')}</div>
              <div className="mt-3 font-display text-lg font-bold text-[var(--mm-ink)]">{s.t}</div>
              <p className="mt-1.5 text-xs text-[var(--mm-ink)]/60 leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Evidence Strip */}
      <Section bg="white">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="mm-kicker mb-3">From Ideas to Classrooms</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] mm-rule">Where the work already lives.</h2>
          </div>
          <Link to="/projects" className="btn-ghost">See all projects <ArrowRight className="w-4 h-4"/></Link>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
          {evidenceStrip.map((e, i) => {
            const Icon = [GraduationCap, Users, Building2, Radio, FileCheck, Wifi][i];
            return (
              <Link key={e.title} to="/projects" className="mm-card p-5 flex flex-col gap-3">
                <div className="w-10 h-10 rounded-full bg-[var(--mm-cream)] text-[var(--mm-purple)] flex items-center justify-center"><Icon className="w-4 h-4"/></div>
                <div>
                  <div className="font-display text-lg font-bold text-[var(--mm-ink)]">{e.title}</div>
                  <p className="mt-1 text-xs text-[var(--mm-ink)]/60 leading-relaxed">{e.body}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* ECCE proof */}
      <Section bg="cream2">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="mm-kicker mb-4">Proven at Grassroots</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] mm-rule">One model. Three scales. Field evidence across states.</h2>
            <p className="mt-6 text-[var(--mm-ink)]/70 leading-relaxed">The same integrated Digital Anganwadi system — device, content, connectivity, worker training and monitoring — has been deployed from a single smart village to a 67-centre government pilot.</p>
            <div className="mt-8"><Link to="/transform/smart-ecce" className="btn-primary">Explore Smart ECCE <ArrowRight className="w-4 h-4"/></Link></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {ecceStats.map((s) => (
              <div key={s.value} className="mm-card p-5 text-center">
                <div className="font-display text-3xl md:text-4xl font-bold text-[var(--mm-purple)]">{s.value}</div>
                <div className="mt-2 text-[13px] font-semibold text-[var(--mm-ink)]">{s.label}</div>
                <div className="mt-1 text-[11px] text-[var(--mm-ink)]/55 leading-snug">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <CTA
        title="Ready to see the model in your classrooms?"
        sub="Talk to our team about Discovery, Pilot or Scale for your school, department or CSR programme."
        primary={{ label: 'Contact Mashmari', href: '/contact' }}
        secondary={{ label: 'Explore Projects', href: '/projects' }}
      />
    </Shell>
  );
}

function TrianglePattern() {
  return (
    <div className="relative w-full max-w-[460px] aspect-square">
      {/* Top */}
      <div className="absolute left-1/2 -translate-x-1/2 top-0">
        <NodeCircle label="Learners" icon={<Users className="w-6 h-6"/>} />
      </div>
      {/* Bottom left */}
      <div className="absolute left-0 bottom-0">
        <NodeCircle label="Teachers" icon={<Sparkles className="w-6 h-6"/>} />
      </div>
      {/* Bottom right */}
      <div className="absolute right-0 bottom-0">
        <NodeCircle label="Institutions" icon={<Briefcase className="w-6 h-6"/>} />
      </div>
      {/* dotted lines */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400" fill="none">
        <line x1="200" y1="80" x2="80" y2="320" stroke="#4A1259" strokeWidth="2" strokeDasharray="3 8" strokeLinecap="round" opacity="0.5"/>
        <line x1="200" y1="80" x2="320" y2="320" stroke="#4A1259" strokeWidth="2" strokeDasharray="3 8" strokeLinecap="round" opacity="0.5"/>
        <line x1="80" y1="320" x2="320" y2="320" stroke="#4A1259" strokeWidth="2" strokeDasharray="3 8" strokeLinecap="round" opacity="0.5"/>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="bg-[var(--mm-cream)]/95 rounded-full px-5 py-2 border border-[var(--mm-line)]">
          <span className="text-[10px] tracking-[0.24em] uppercase text-[var(--mm-purple)] font-semibold">Classroom Execution</span>
        </div>
      </div>
    </div>
  );
}

function NodeCircle({ label, icon }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <DotCircle size={120}>{icon}</DotCircle>
      <span className="text-[11px] tracking-[0.24em] uppercase text-[var(--mm-purple)] font-semibold">{label}</span>
    </div>
  );
}
